// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getIsNonPurchasable, xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import Previous from "@keller/XDL/Icons/Previous";
import { mergeWithCss, parseBoolean } from "@keller/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    extendedStyles?: TypographyPresentationProps;
}

const productPreviousPurchaseStyles: TypographyPresentationProps = {
    size: 12,
    weight: "bold",
    color: "success",
    css: css`
        letter-spacing: 0;
        line-height: 12px;
        display: flex;
        align-items: center;
        ${IconWrapper} {
            margin-right: 4px;
        }
    `,
};

const ProductPreviousPurchase: FC<OwnProps> = ({ product, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(productPreviousPurchaseStyles, extendedStyles));
    const isAuthenticated = useAppSelector(state => xIsAuthenticated(state));
    const isNonPurchasable = useAppSelector(state => getIsNonPurchasable(state));

    const isPreviouslyPurchased = parseBoolean(product?.properties?.isPreviouslyPurchased);
    if (!isPreviouslyPurchased || isNonPurchasable || !isAuthenticated) {
        return null;
    }

    return (
        <Typography {...styles} data-test-selector="ProductPreviousPurchase">
            <Icon src={Previous} size={12} color="success" />
            {translate("Previous Purchase")}
        </Typography>
    );
};

export default ProductPreviousPurchase;
